import { useEffect, useState, useContext } from "react";
import CountryContext from "../CountryContext";
import useCountryAxiosInstance from "./useCountryAxiosInstance";
import evaluateBlockDependencies from "../utils/evaluateBlockDependencies";
import _ from "lodash";
import explodeSurveyResponses from "../utils/explodeSurveyResponses";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";


export const useServicesChecklists = (worksiteId, defaultValue=[], observable=null) => {
  const [checklists, setChecklists] = useState(defaultValue);
  const { country } = useContext(CountryContext);
  const countryApiRequest = useCountryAxiosInstance(country?.code);
  const getConfigurations = useGetServiceConfiguration(false);

  useEffect(() => {
    const fetch = async () => {

      // fetch tasks
      const existingTasks = await countryApiRequest.get("/worksites_tasks/all", {params: {id_worksite: worksiteId}});
      const existingTasksSurveysIds = existingTasks.reduce((acc, {id_survey}) => acc.add(id_survey), new Set());
      
      // fetch all checklists (will be needed in any case)
      const allChecklists = await countryApiRequest.get("/services_checklists/all?join=service", {params: {flg_active: true}});
      const checklists = allChecklists.filter((c) => c.service?.active === true).map((c) => ({...c, surveys: []}));
      const checklistsServicesIds = [...(new Set(checklists.map((c) => c.id_service)))];

      // fetch surveys for the current worksite
      const unfilteredSurveys = await countryApiRequest.get(
        `/service_surveys/by_services_ids/${checklistsServicesIds.join(',')}`,
        {params: {worksite_id: worksiteId}}
      )
      const surveys = unfilteredSurveys.filter((s) => !existingTasksSurveysIds.has(s.id));

      // if no surveys have been created for the current worksite, simply return the full list; otherwise, fetch the
      // entities needed for the dependencies check (involved configurations and responses) and perform it in order
      // to determine if the checklists must be considered templates
      if (surveys.length > 0) {

        // fetch involved responses and configurations (needed for the dependencies check)
        const responses = await countryApiRequest.get(
          `/service_survey_responses/by_surveys_ids/${surveys.map((s) => s.id).join(',')}`,
        );

        // group the retrieved entities by services for ease of retrieval
        const responsesGroups = {};
        const configurationsCache = {};
        const getConfigurationsCacheKey = (serviceId, version) => `${serviceId}_V${version}`;
        for (const s of surveys) {

          if (!(s.service_id in responsesGroups))
            responsesGroups[s.service_id] = {};

          // fetch configurations for the current survey
          const configurationsKey = getConfigurationsCacheKey(s.service_id, s.service_version);
          if (!(configurationsKey in configurationsCache))
            configurationsCache[configurationsKey] = await getConfigurations(s.service_id, s.service_version);
          const configurations = configurationsCache[configurationsKey];

          // split survey responses in recursive and non-recursive (immediate)
          const surveyResponses = responses.filter((r) => r.service_survey_id === s.id);
          const {groups, containsRecursive} = explodeSurveyResponses(
            surveyResponses, configurations
          );
          groups.forEach((g, idx) => {
            const key = !containsRecursive ? s.id : `${s.id}_unnested-${idx}`;
            responsesGroups[s.service_id][key] = {
              surveyId: s.id,
              serviceVersion: s.service_version,
              responses: g,
              created_at: s.created_at
            };
          })
        }

        // iterate each checklist in order to determine if its dependencies are verified by at least one survey
        checklists.forEach((c, idx, arr) => {
          const surveysData = Object.values(responsesGroups[c.id_service] ?? {});

          const matches = [];
          if (surveysData.length > 0) {
            surveysData.forEach(({surveyId, serviceVersion, responses}) => {
              // check if the target survey already matched: if so, skip it
              if (matches.some((m) => m.id === surveyId))
                return;

              // check if the current survey is compliant with the current checklist's dependencies; if not, skip it
              const context = {
                configuration: configurationsCache[getConfigurationsCacheKey(c.id_service, serviceVersion)],
                responses,
              };
              const dependenciesVerified = evaluateBlockDependencies(c.dependencies, context, true);
              if (!dependenciesVerified)
                return;

              // if the dependencies are verified
              const targetSurvey = surveys.find(({id}) => id === surveyId);
              if (!targetSurvey) {
                console.warn(`Survey having ID=${surveyId} matched checklist dependencies but can't be found`);
                return;
              }
              matches.push(_.pick(targetSurvey, ['id', 'name', 'created_at']));
            });
          }

          // update the checklist's information
          arr[idx] = {...c, surveys: matches};
        })
      }

      setChecklists(checklists);
    };

    // noinspection JSIgnoredPromiseFromCall
    fetch();
  }, [country.code, worksiteId, countryApiRequest, observable]);

  return [checklists];
};
